import type {
  IDBPDatabase,
} from 'idb';

import type {
  BackupErrorCode,
  BackupResult,
  LogicalBackupSnapshot,
} from '../domain/backup.ts';

import type {
  BackupRepository,
} from '../ports/backup-repository.ts';

import {
  openFenixDatabase,
  validDatasetControl,
} from './fenix-db.ts';

import type {
  FenixSchema,
} from './fenix-db.ts';

const BACKUP_STORES = [
  'control',
  'heads',
  'history',

  'training_exercises',
  'training_exercise_revisions',
  'training_routines',
  'training_routine_revisions',
  'training_planned_sessions',
  'training_planned_session_history',
  'training_executions',
  'training_working_sets',

  'nutrition_recipes',
  'nutrition_recipe_revisions',
  'nutrition_planned_meals',
  'nutrition_planned_meal_history',
  'nutrition_consumptions',
  'nutrition_consumption_history',
  'nutrition_shopping_items',
  'nutrition_shopping_item_history',

  'progress_weights',
  'progress_weight_history',
] as const;

function failure<T = never>(
  code: BackupErrorCode,
): BackupResult<T> {
  return {
    ok: false,
    code,
  };
}

function openedFailure(
  code: string,
): BackupErrorCode {
  switch (code) {
    case 'SCHEMA_UNSUPPORTED':
      return 'SCHEMA_UNSUPPORTED';

    case 'UPGRADE_BLOCKED':
      return 'UPGRADE_BLOCKED';

    case 'CORRUPTION_DETECTED':
      return 'CORRUPTION_DETECTED';

    case 'TRANSACTION_ABORTED':
      return 'TRANSACTION_ABORTED';

    case 'STORAGE_QUOTA':
      return 'STORAGE_QUOTA';

    default:
      return 'STORAGE_UNAVAILABLE';
  }
}

function storageFailure(
  error: unknown,
): BackupErrorCode {
  const name =
    error instanceof Error
      ? error.name
      : '';

  switch (name) {
    case 'QuotaExceededError':
      return 'STORAGE_QUOTA';

    case 'AbortError':
      return 'TRANSACTION_ABORTED';

    case 'VersionError':
      return 'SCHEMA_UNSUPPORTED';

    case 'ConstraintError':
    case 'DataError':
      return 'CORRUPTION_DETECTED';

    default:
      return 'STORAGE_UNAVAILABLE';
  }
}

function activeRecords<T>(
  rows: readonly {
    datasetId: string;
    record: T;
  }[],
  datasetId: string,
): T[] {
  return rows
    .filter(
      row =>
        row.datasetId === datasetId,
    )
    .map(
      row =>
        row.record,
    );
}

export class IndexedBackupRepository
implements BackupRepository {
  private readonly db:
    IDBPDatabase<FenixSchema>;

  constructor(
    db: IDBPDatabase<FenixSchema>,
  ) {
    this.db = db;
  }

  async readLogicalSnapshot():
    Promise<BackupResult<LogicalBackupSnapshot>> {
    try {
      /*
       * All logical backup authority is read inside ONE
       * readonly IndexedDB transaction.
       *
       * A concurrent writer is therefore observed wholly
       * before or wholly after the snapshot, never as a
       * cross-store partial state.
       */
      const tx =
        this.db.transaction(
          [...BACKUP_STORES],
          'readonly',
        );

      const [
        control,

        heads,
        dailyHistory,

        trainingExercises,
        trainingExerciseRevisions,
        trainingRoutines,
        trainingRoutineRevisions,
        trainingPlannedSessions,
        trainingPlannedHistory,
        trainingExecutions,
        trainingWorkingSets,

        nutritionRecipes,
        nutritionRecipeRevisions,
        nutritionPlannedMeals,
        nutritionPlannedHistory,
        nutritionConsumptions,
        nutritionConsumptionHistory,
        nutritionShoppingItems,
        nutritionShoppingHistory,

        progressWeights,
        progressWeightHistory,
      ] = await Promise.all([
        tx.objectStore('control')
          .get('control'),

        tx.objectStore('heads')
          .getAll(),

        tx.objectStore('history')
          .getAll(),

        tx.objectStore('training_exercises')
          .getAll(),

        tx.objectStore('training_exercise_revisions')
          .getAll(),

        tx.objectStore('training_routines')
          .getAll(),

        tx.objectStore('training_routine_revisions')
          .getAll(),

        tx.objectStore('training_planned_sessions')
          .getAll(),

        tx.objectStore('training_planned_session_history')
          .getAll(),

        tx.objectStore('training_executions')
          .getAll(),

        tx.objectStore('training_working_sets')
          .getAll(),

        tx.objectStore('nutrition_recipes')
          .getAll(),

        tx.objectStore('nutrition_recipe_revisions')
          .getAll(),

        tx.objectStore('nutrition_planned_meals')
          .getAll(),

        tx.objectStore('nutrition_planned_meal_history')
          .getAll(),

        tx.objectStore('nutrition_consumptions')
          .getAll(),

        tx.objectStore('nutrition_consumption_history')
          .getAll(),

        tx.objectStore('nutrition_shopping_items')
          .getAll(),

        tx.objectStore('nutrition_shopping_item_history')
          .getAll(),

        tx.objectStore('progress_weights')
          .getAll(),

        tx.objectStore('progress_weight_history')
          .getAll(),
      ] as const);

      await tx.done;

      if (!validDatasetControl(control)) {
        return failure(
          'CORRUPTION_DETECTED',
        );
      }

      /*
       * Do not export a provisional Restore target.
       * A quiescent dataset cannot retain recoveryDatasetId.
       */
      if (control.restoreFence !== null) {
        return failure(
          'RESTORE_IN_PROGRESS',
        );
      }

      if (control.recoveryDatasetId !== null) {
        return failure(
          'CORRUPTION_DETECTED',
        );
      }

      const datasetId =
        control.activeDatasetId;

      const workingSets =
        trainingWorkingSets
          .filter(
            row =>
              row.datasetId === datasetId,
          )
          .map(
            row => ({
              position: row.position,
              workingSet: row.record,
            }),
          )
          .sort(
            (left, right) =>
              left.workingSet.executedSessionId
                .localeCompare(
                  right.workingSet.executedSessionId,
                )
              || left.position - right.position,
          );

      return {
        ok: true,

        value: {
          schemaVersion:
            control.schemaVersion,

          dataset: {
            training: {
              exercises:
                activeRecords(
                  trainingExercises,
                  datasetId,
                ),

              routines:
                activeRecords(
                  trainingRoutines,
                  datasetId,
                ),

              plannedSessions:
                activeRecords(
                  trainingPlannedSessions,
                  datasetId,
                ),

              executions:
                activeRecords(
                  trainingExecutions,
                  datasetId,
                ),

              workingSets,
            },

            nutrition: {
              recipes:
                activeRecords(
                  nutritionRecipes,
                  datasetId,
                ),

              plannedMeals:
                activeRecords(
                  nutritionPlannedMeals,
                  datasetId,
                ),

              consumptions:
                activeRecords(
                  nutritionConsumptions,
                  datasetId,
                ),

              shoppingItems:
                activeRecords(
                  nutritionShoppingItems,
                  datasetId,
                ),
            },

            progress: {
              weights:
                activeRecords(
                  progressWeights,
                  datasetId,
                ),
            },

            dailyRoutine: {
              checks:
                heads
                  .filter(
                    row =>
                      row.datasetId === datasetId,
                  )
                  .map(
                    row =>
                      row.check,
                  ),
            },

            /*
             * No persisted Settings facts exist yet.
             * The logical owner is explicit, but empty.
             */
            settings: {},
          },

          revisionHistory: {
            dailyRoutine: {
              checks:
                dailyHistory
                  .filter(
                    row =>
                      row.datasetId === datasetId,
                  )
                  .map(
                    row =>
                      row.check,
                  ),
            },

            training: {
              exercises:
                activeRecords(
                  trainingExerciseRevisions,
                  datasetId,
                ),

              routines:
                activeRecords(
                  trainingRoutineRevisions,
                  datasetId,
                ),

              plannedSessions:
                activeRecords(
                  trainingPlannedHistory,
                  datasetId,
                ),
            },

            nutrition: {
              recipes:
                activeRecords(
                  nutritionRecipeRevisions,
                  datasetId,
                ),

              plannedMeals:
                activeRecords(
                  nutritionPlannedHistory,
                  datasetId,
                ),

              consumptions:
                activeRecords(
                  nutritionConsumptionHistory,
                  datasetId,
                ),

              shoppingItems:
                activeRecords(
                  nutritionShoppingHistory,
                  datasetId,
                ),
            },

            progress: {
              weights:
                activeRecords(
                  progressWeightHistory,
                  datasetId,
                ),
            },
          },

          /*
           * G6-A exports no product binaries.
           * G6-C owns explicitly authorized media.
           */
          mediaManifest: {
            entries: [],
          },
        },
      };
    }
    catch (error) {
      return failure(
        storageFailure(error),
      );
    }
  }
}

export async function openBackupRepository(
  databaseName: string,
): Promise<BackupResult<IndexedBackupRepository>> {
  const opened =
    await openFenixDatabase(
      databaseName,
    );

  if (!opened.ok) {
    return failure(
      openedFailure(
        opened.code,
      ),
    );
  }

  return {
    ok: true,
    value:
      new IndexedBackupRepository(
        opened.value,
      ),
  };
}